import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { ActividadEquipo, EncuestaEquipo, EquipoCanal, EquipoMensaje, EstadoEquipo } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { useFotos } from "../../../appMovil/fotos";
import { cuando, errorTexto, fechaLarga } from "./salesHelpers";
import { BotonEmoji } from "./equipoEmojis";
import { resumenTexto } from "./equipoGifs";
import { sinAcentos } from "./buscarMensajes";
import { FotoPersona } from "./hiloPanel";

const http = HttpManager.getInstance();

/* ───────── Horas: todo se calcula en hora de Ciudad de México (= Honduras, UTC-6) y se manda en UTC ───────── */

const MX = 6 * 3600000;
/** "Dentro de N días a las H:MM" en hora de Ciudad de México, como fecha UTC (ISO). */
export const aLasMx = (dias: number, h: number, mi = 0): string => {
	const ahora = new Date(Date.now() - MX);
	return new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth(), ahora.getUTCDate() + dias, h, mi) + MX).toISOString();
};
export const enMinutos = (m: number): string => new Date(Date.now() + m * 60000).toISOString();
/** Días que faltan para el próximo lunes (1-7). */
const alLunes = () => { const d = new Date(Date.now() - MX).getUTCDay(); return ((8 - d) % 7) || 7; };
/** "2026-10-27T09:30" (lo que da un <input type="datetime-local">, leído como hora de México) → UTC ISO. */
const deLocalMx = (v: string): string | null => {
	const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v);
	return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) + MX).toISOString() : null;
};
/** Lo contrario, para el valor mínimo del campo: ahora en hora de México. */
const ahoraLocalMx = () => new Date(Date.now() - MX + 60000).toISOString().slice(0, 16);
/** "hasta las 2:00 pm", "hasta mañana 9:00 am", "hasta 30 Oct". */
export const hastaTexto = (iso: string | null | undefined): string => (iso ? "hasta " + cuando(iso).replace(/^hoy /, "las ") : "");

interface Opcion { t: string; v: () => string }
/** Lista de horas para elegir (recordar, enviar más tarde, pausar avisos) con "Otra hora…". */
export const MenuHoras: React.FC<{ titulo: string; opciones: Opcion[]; onElegir: (iso: string) => void; onCerrar: () => void; className?: string }> = ({ titulo, opciones, onElegir, onCerrar, className }) => {
	const [otra, setOtra] = useState(false);
	const [v, setV] = useState("");
	const caja = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) onCerrar(); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", esc); };
	}, [onCerrar]);
	return (
		<div ref={caja} className={"ex-menu" + (className ? " " + className : "")} role="menu" onClick={(e) => e.stopPropagation()}>
			<div className="ex-menu-tit">{titulo}</div>
			{opciones.map((o) => <button type="button" role="menuitem" key={o.t} onClick={() => onElegir(o.v())}>{o.t}</button>)}
			{otra ? (
				<div className="ex-otra">
					<input type="datetime-local" value={v} min={ahoraLocalMx()} onChange={(e) => setV(e.target.value)} aria-label="Fecha y hora" autoFocus />
					<button type="button" className="btn-wa pri" disabled={!deLocalMx(v)} onClick={() => { const x = deLocalMx(v); if (x) onElegir(x); }}>Listo</button>
				</div>
			) : <button type="button" role="menuitem" onClick={() => setOtra(true)}>Otra hora…</button>}
		</div>
	);
};

export const OPCIONES_RECORDAR: Opcion[] = [
	{ t: "En 20 minutos", v: () => enMinutos(20) },
	{ t: "En 1 hora", v: () => enMinutos(60) },
	{ t: "En 3 horas", v: () => enMinutos(180) },
	{ t: "Mañana a las 9:00 am", v: () => aLasMx(1, 9) },
	{ t: "El lunes a las 9:00 am", v: () => aLasMx(alLunes(), 9) },
];
export const OPCIONES_PROGRAMAR: Opcion[] = [
	{ t: "Mañana a las 8:00 am", v: () => aLasMx(1, 8) },
	{ t: "Mañana a las 9:00 am", v: () => aLasMx(1, 9) },
	{ t: "El lunes a las 8:00 am", v: () => aLasMx(alLunes(), 8) },
];

/* ───────── Estado y pausar avisos ───────── */

const PRESETS: [string, string][] = [["🏫", "En clase"], ["📞", "En llamada"], ["🤝", "En junta"], ["🍽️", "Comiendo"], ["🚗", "En camino"], ["🤒", "Enferma"], ["🌴", "De vacaciones"]];
const DURACIONES: { t: string; v: () => string | null }[] = [
	{ t: "No borrar", v: () => null }, { t: "30 minutos", v: () => enMinutos(30) }, { t: "1 hora", v: () => enMinutos(60) },
	{ t: "4 horas", v: () => enMinutos(240) }, { t: "Hoy", v: () => aLasMx(0, 23, 59) }, { t: "Esta semana", v: () => aLasMx(Math.max(1, alLunes() - 1), 23, 59) },
];
const PAUSAS: Opcion[] = [
	{ t: "30 minutos", v: () => enMinutos(30) }, { t: "1 hora", v: () => enMinutos(60) }, { t: "2 horas", v: () => enMinutos(120) },
	{ t: "Hasta mañana 8:00 am", v: () => aLasMx(1, 8) },
];
/** La ventanita de avisos lee esto para no sonar mientras están pausados (además el servidor no manda al celular). */
export const pausaHasta = (): number => { try { return Number(localStorage.getItem("wappteam.pausa") || 0); } catch { return 0; } };
const guardarPausa = (iso: string | null) => { try { if (iso) localStorage.setItem("wappteam.pausa", String(Date.parse(iso))); else localStorage.removeItem("wappteam.pausa"); } catch { /* sin memoria */ } };

/** Mi estado, arriba de la lista de WappTeam: como Slack, "Actualizar tu estado" y "Pausar avisos". */
export const EstadoMenu: React.FC<{ campusId: number; estado: EstadoEquipo | null; onCambio: (e: EstadoEquipo | null) => void; onCerrar: () => void }> = ({ campusId, estado, onCambio, onCerrar }) => {
	const [emoji, setEmoji] = useState(estado?.emoji || "");
	const [texto, setTexto] = useState(estado?.texto || "");
	const [dur, setDur] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const caja = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node) && !(e.target as HTMLElement).closest?.(".eqg-panel")) onCerrar(); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", esc); };
	}, [onCerrar]);
	const guardar = async (d: Parameters<typeof http.prospectsService.equipoGuardarEstado>[1], cerrar = true) => {
		setOcupado(true);
		try {
			const r = await http.prospectsService.equipoGuardarEstado(campusId, d);
			if ("noMolestarHasta" in d) guardarPausa(d.noMolestarHasta || null);
			onCambio(r.data.estado);
			if (cerrar) onCerrar();
		} catch (e) { setError(errorTexto(e)); }
		setOcupado(false);
	};
	const pausado = !!estado?.noMolestarHasta;
	return (
		<div ref={caja} className="ex-estado" role="dialog" aria-label="Tu estado" onClick={(e) => e.stopPropagation()}>
			<div className="ex-estado-tit">Tu estado</div>
			<div className="ex-estado-campo">
				<BotonEmoji compacto onElegir={(e) => setEmoji(e)} />
				{emoji ? <span className="ex-estado-emoji" aria-hidden="true">{emoji}</span> : null}
				<input value={texto} maxLength={100} onChange={(e) => setTexto(e.target.value)} placeholder="¿Qué estás haciendo?" aria-label="Estado" />
				{emoji || texto ? <button type="button" className="ex-limpiar" onClick={() => { setEmoji(""); setTexto(""); }} aria-label="Borrar"><WappIcono n="x" /></button> : null}
			</div>
			<div className="ex-presets">
				{PRESETS.map(([e, t]) => <button type="button" key={t} className={emoji === e && texto === t ? "on" : ""} onClick={() => { setEmoji(e); setTexto(t); }}><span>{e}</span>{t}</button>)}
			</div>
			<label className="ex-dur">Quitar después de
				<select value={dur} onChange={(e) => setDur(Number(e.target.value))}>{DURACIONES.map((d, i) => <option key={d.t} value={i}>{d.t}</option>)}</select>
			</label>
			<div className="fila-botones">
				<button type="button" className="btn-wa pri" disabled={ocupado} onClick={() => guardar({ emoji: emoji || null, texto: texto.trim() || null, hasta: emoji || texto.trim() ? DURACIONES[dur].v() : null })}>Guardar</button>
				{estado?.emoji || estado?.texto ? <button type="button" className="btn-wa" disabled={ocupado} onClick={() => guardar({ emoji: null, texto: null, hasta: null })}>Quitar estado</button> : null}
			</div>
			<div className="ex-pausa">
				<div className="ex-pausa-tit"><WappIcono n="bellOff" /><b>Pausar avisos</b>{pausado ? <span>{hastaTexto(estado!.noMolestarHasta)}</span> : null}</div>
				{pausado ? <button type="button" className="btn-wa" disabled={ocupado} onClick={() => guardar({ noMolestarHasta: null }, false)}>Reanudar avisos</button> : (
					<div className="ex-pausa-op">{PAUSAS.map((p) => <button type="button" key={p.t} disabled={ocupado} onClick={() => guardar({ noMolestarHasta: p.v() }, false)}>{p.t}</button>)}</div>
				)}
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
		</div>
	);
};

/* ───────── Encuestas ───────── */

export const EncuestaModal: React.FC<{ campusId: number; canalId: number; hiloId?: number | null; onCerrar: () => void; onCreada: () => void }> = ({ campusId, canalId, hiloId, onCerrar, onCreada }) => {
	const [pregunta, setPregunta] = useState("");
	const [ops, setOps] = useState(["", ""]);
	const [multiple, setMultiple] = useState(false);
	const [anonima, setAnonima] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const listas = ops.filter((o) => o.trim()).length;
	const crear = async () => {
		setOcupado(true);
		try {
			await http.prospectsService.equipoCrearEncuesta(campusId, { canalId, pregunta, opciones: ops.filter((o) => o.trim()), multiple, anonima, hiloId });
			onCreada();
			onCerrar();
		} catch (e) { setError(errorTexto(e)); }
		setOcupado(false);
	};
	return (
		<div className="ex-velo" onClick={onCerrar}>
			<div className="ex-modal" role="dialog" aria-label="Nueva encuesta" onClick={(e) => e.stopPropagation()}>
				<div className="ex-modal-cab"><b>Nueva Encuesta</b><button type="button" className="hp-ico" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button></div>
				{/* div, no label: el label global de WAPP (style.css) es position: absolute y lo dejaba flotando sobre el título. */}
				<div className="ex-campo"><span>Pregunta</span><input autoFocus value={pregunta} maxLength={300} onChange={(e) => setPregunta(e.target.value)} placeholder="¿Qué día hacemos la junta?" aria-label="Pregunta" /></div>
				<div className="ex-campo"><span>Opciones</span>
					{ops.map((o, i) => (
						<div key={i} className="ex-op">
							<input value={o} maxLength={120} onChange={(e) => setOps(ops.map((x, j) => (j === i ? e.target.value : x)))} placeholder={"Opción " + (i + 1)}
								onKeyDown={(e) => { if (e.key === "Enter" && i === ops.length - 1 && ops.length < 10) { e.preventDefault(); setOps([...ops, ""]); } }} />
							{ops.length > 2 ? <button type="button" className="ex-limpiar" onClick={() => setOps(ops.filter((_, j) => j !== i))} aria-label={"Quitar opción " + (i + 1)}><WappIcono n="x" /></button> : null}
						</div>
					))}
					{ops.length < 10 ? <button type="button" className="enlace ex-mas" onClick={() => setOps([...ops, ""])}>+ Agregar opción</button> : null}
				</div>
				<label className="eq-check"><input type="checkbox" checked={multiple} onChange={(e) => setMultiple(e.target.checked)} /> Se puede elegir más de una</label>
				<label className="eq-check"><input type="checkbox" checked={anonima} onChange={(e) => setAnonima(e.target.checked)} /> Anónima: no se ve quién votó qué</label>
				{error ? <div className="aviso error">{error}</div> : null}
				<div className="fila-botones">
					<button type="button" className="btn-wa pri" disabled={ocupado || !pregunta.trim() || listas < 2} onClick={crear}>Mandar Encuesta</button>
					<button type="button" className="btn-wa" onClick={onCerrar}>Cancelar</button>
				</div>
			</div>
		</div>
	);
};

/** La encuesta dentro del mensaje: barras con porcentaje, mi voto marcado, quiénes votaron (si no es anónima). */
export const EncuestaMensaje: React.FC<{ campusId: number; m: EquipoMensaje & { encuesta: EncuestaEquipo }; esMia: boolean; puedeCerrar: boolean; onCambio: () => void }> = ({ campusId, m, esMia, puedeCerrar, onCambio }) => {
	const e = m.encuesta;
	const [mios, setMios] = useState<number[]>(e.mios);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => setMios(e.mios), [e.mios]);
	const total = e.opciones.reduce((t, o) => t + o.n, 0);
	const votar = async (i: number) => {
		if (e.cerrada) return;
		const nuevos = e.multiple ? (mios.includes(i) ? mios.filter((x) => x !== i) : [...mios, i]) : (mios.includes(i) ? [] : [i]);
		setMios(nuevos);
		try { await http.prospectsService.equipoVotar(campusId, m.id, nuevos); onCambio(); } catch (er) { setError(errorTexto(er)); setMios(e.mios); }
	};
	const cerrar = async () => {
		if (!window.confirm("¿Cerrar la encuesta? Ya nadie podrá votar.")) return;
		try { await http.prospectsService.equipoCerrarEncuesta(campusId, m.id); onCambio(); } catch (er) { setError(errorTexto(er)); }
	};
	return (
		<div className={"ex-encuesta" + (e.cerrada ? " cerrada" : "")} onClick={(ev) => ev.stopPropagation()}>
			<div className="ex-enc-cab"><span aria-hidden="true">📊</span><b>{e.pregunta}</b></div>
			<div className="ex-enc-ops" role={e.multiple ? "group" : "radiogroup"}>
				{e.opciones.map((o, i) => {
					const pct = total ? Math.round((o.n / total) * 100) : 0;
					const mia = mios.includes(i);
					return (
						<button type="button" key={i} className={"ex-enc-op" + (mia ? " mia" : "")} disabled={e.cerrada} onClick={() => votar(i)}
							role={e.multiple ? "checkbox" : "radio"} aria-checked={mia} title={o.quienes.length ? o.quienes.join(", ") : undefined}>
							<span className="ex-enc-barra" style={{ width: pct + "%" }} aria-hidden="true" />
							<span className={"ex-enc-marca" + (e.multiple ? " cuadro" : "")} aria-hidden="true">{mia ? "✓" : ""}</span>
							<span className="ex-enc-txt">{o.texto}</span>
							<span className="ex-enc-n">{o.n ? pct + "% · " + o.n : ""}</span>
						</button>
					);
				})}
			</div>
			<div className="ex-enc-pie">
				<span>{e.votantes} {e.votantes === 1 ? "persona votó" : "personas votaron"}{e.multiple ? " · varias respuestas" : ""}{e.anonima ? " · anónima" : ""}{e.cerrada ? " · cerrada" : ""}</span>
				{!e.cerrada && (esMia || puedeCerrar) ? <button type="button" className="enlace" onClick={cerrar}>Cerrar Encuesta</button> : null}
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
		</div>
	);
};

/* ───────── Actividad ───────── */

const corto = (body: string | null, tipo: string | null) => resumenTexto(body) || (tipo === "audio" ? "Nota de voz" : tipo === "image" ? "Foto" : tipo === "video" ? "Video" : tipo ? "Archivo" : "Mensaje");

/** "Actividad" como Slack: me mencionaron, me respondieron o reaccionaron a lo mío (30 días). */
export const ActividadVista: React.FC<{ campusId: number; onCerrar: () => void; onIr: (canalId: number, mensajeId: number, hiloId: number | null) => void;
	onPerfil: (userId: number, e: React.MouseEvent) => void }> = ({ campusId, onCerrar, onIr, onPerfil }) => {
	const fotos = useFotos();
	const [items, setItems] = useState<ActividadEquipo[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [filtro, setFiltro] = useState<"todo" | ActividadEquipo["tipo"]>("todo");
	useEffect(() => {
		http.prospectsService.equipoActividad(campusId).then((r) => setItems(r.data.actividad)).catch((e) => setError(errorTexto(e)));
	}, [campusId]);
	const vista = (items || []).filter((x) => filtro === "todo" || x.tipo === filtro);
	const que = (x: ActividadEquipo) => (x.tipo === "mencion" ? "te mencionó" : x.tipo === "respuesta" ? (x.hilo_id ? "respondió en tu hilo" : "respondió a tu mensaje") : "reaccionó " + (x.emoji || "") + " a tu mensaje");
	return (
		<div className="ventas-cliente hv">
			<div className="hv-cab"><b>Actividad</b><span>Menciones, respuestas y reacciones a lo tuyo</span>
				<button type="button" className="hp-ico" onClick={onCerrar} aria-label="Cerrar Actividad"><WappIcono n="x" /></button></div>
			<div className="eq-filtros ex-act-filtros">
				{([["todo", "Todo"], ["mencion", "Menciones"], ["respuesta", "Respuestas"], ["reaccion", "Reacciones"]] as const).map(([k, t]) => (
					<button type="button" key={k} className={"eq-filtro" + (filtro === k ? " on" : "")} onClick={() => setFiltro(k)}>{t}</button>
				))}
			</div>
			<div className="hv-lista">
				{items === null ? <div className="hp-vacio">{error || "Cargando…"}</div>
					: !vista.length ? <div className="hv-vacio"><WappIcono n="bell" /><b>Nada por aquí</b><span>Cuando alguien te mencione, te responda o reaccione a tus mensajes, sale aquí.</span></div>
					: vista.map((x) => (
						<button type="button" key={x.tipo + x.id + (x.emoji || "") + x.user_id} className="hv-item ex-act" onClick={() => onIr(x.canal_id, x.id, x.hilo_id)}>
							<span className="ex-act-fila">
								<FotoPersona id={x.user_id} nombre={x.autor} fotos={fotos} onClick={(e) => { e.stopPropagation(); onPerfil(x.user_id, e); }} />
								<span className="ex-act-cuerpo">
									<span className="ex-act-tit"><b>{x.autor}</b> {que(x)} <span className="ex-act-donde">en {x.canal}</span></span>
									<span className="hv-txt">{x.tipo === "reaccion" ? corto(x.body, x.file_tipo) : corto(x.body, x.file_tipo)}</span>
									{x.tipo === "respuesta" && x.o_body ? <span className="ex-act-cita">Tu mensaje: {x.o_body}</span> : null}
								</span>
								<span className="ex-act-cuando">{cuando(x.created_at)}</span>
							</span>
						</button>
					))}
			</div>
		</div>
	);
};

/* ───────── Archivos y enlaces de una conversación ───────── */

export const AdjuntosPanel: React.FC<{ campusId: number; canalId: number; onCerrar: () => void; onIr: (mensajeId: number, hiloId: number | null) => void }> = ({ campusId, canalId, onCerrar, onIr }) => {
	const [que, setQue] = useState<"archivos" | "enlaces">("archivos");
	const [datos, setDatos] = useState<{ archivos?: any[]; enlaces?: any[] } | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		setDatos(null);
		http.prospectsService.equipoAdjuntos(campusId, canalId, que).then((r) => setDatos(r.data)).catch((e) => setError(errorTexto(e)));
	}, [campusId, canalId, que]);
	const icono = (t: string) => (t === "image" ? "photo" : t === "video" ? "video" : t === "audio" ? "audio" : "doc");
	const tam = (b: number | null) => (!b ? "" : b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");
	const lista = que === "archivos" ? datos?.archivos : datos?.enlaces;
	return (
		<aside className="hp" aria-label="Archivos y enlaces">
			<div className="hp-barra"><div className="hp-titulo"><b>Archivos y Enlaces</b><span>De esta conversación</span></div>
				<button type="button" className="hp-ico" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button></div>
			<div className="ex-tabs" role="tablist">
				<button type="button" role="tab" aria-selected={que === "archivos"} className={que === "archivos" ? "on" : ""} onClick={() => setQue("archivos")}>Archivos</button>
				<button type="button" role="tab" aria-selected={que === "enlaces"} className={que === "enlaces" ? "on" : ""} onClick={() => setQue("enlaces")}>Enlaces</button>
			</div>
			<div className="hp-lista">
				{!datos ? <div className="hp-vacio">{error || "Cargando…"}</div>
					: !lista || !lista.length ? <div className="hp-vacio">{que === "archivos" ? "Todavía no se ha mandado ningún archivo aquí." : "Todavía no se ha mandado ningún enlace aquí."}</div>
					: que === "archivos" ? lista.map((a) => (
						<button type="button" key={a.id} className="ex-adj" onClick={() => onIr(a.id, a.hilo_id)} title="Ir al mensaje">
							<span className="ex-adj-ico"><WappIcono n={icono(a.file_tipo) as any} /></span>
							<span className="ex-adj-txt"><b>{a.file_name || (a.file_tipo === "audio" ? "Nota de voz" : a.file_tipo === "image" ? "Foto" : a.file_tipo === "video" ? "Video" : "Archivo")}</b>
								<small>{a.author_name.split(" ")[0]} · {cuando(a.created_at)}{a.file_size ? " · " + tam(a.file_size) : ""}</small></span>
						</button>
					)) : lista.map((l, i) => (
						<div key={l.id + "-" + i} className="ex-adj">
							<span className="ex-adj-ico"><WappIcono n="link" /></span>
							<span className="ex-adj-txt"><a href={l.url} target="_blank" rel="noopener noreferrer">{l.url.replace(/^https?:\/\/(www\.)?/, "")}</a>
								<small>{l.autor.split(" ")[0]} · {cuando(l.created_at)} · <button type="button" className="enlace" onClick={() => onIr(l.id, l.hilo_id)}>Ir al mensaje</button></small></span>
						</div>
					))}
			</div>
		</aside>
	);
};

/* ───────── Ir a… (Cmd/Ctrl + K) ───────── */

/** Saltar a una conversación escribiendo su nombre, como Slack (Cmd/Ctrl + K). */
export const SaltoRapido: React.FC<{ canales: EquipoCanal[]; onElegir: (id: number) => void; onAtajo: (k: "hilos" | "actividad") => void; onCerrar: () => void }> = ({ canales, onElegir, onAtajo, onCerrar }) => {
	const [q, setQ] = useState("");
	const [i, setI] = useState(0);
	const items = useMemo(() => {
		const t = sinAcentos(q.trim());
		const base: { k: string; t: string; sub: string; ir: () => void; pendientes?: number }[] = [];
		const cs = canales.filter((c) => !t || sinAcentos(c.nombre || "").includes(t))
			.sort((a, b) => (t ? sinAcentos(a.nombre).indexOf(t) - sinAcentos(b.nombre).indexOf(t) : 0)).slice(0, 12);
		return [...cs.map((c) => ({ k: "c" + c.id, t: (c.tipo === "canal" ? "# " : "") + c.nombre, sub: c.tipo === "canal" ? "Canal" : c.tipo === "grupo" ? "Grupo" : "Mensaje directo",
			ir: () => onElegir(c.id), pendientes: c.no_leidos })), ...base];
	}, [q, canales, onElegir]);
	useEffect(() => setI(0), [q]);
	return (
		<div className="ex-velo arriba" onClick={onCerrar}>
			<div className="ex-salto" role="dialog" aria-label="Ir a una conversación" onClick={(e) => e.stopPropagation()}>
				<div className="ex-salto-campo">
					<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
					<input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ir a una conversación, canal o persona…" aria-label="Ir a"
						onKeyDown={(e) => {
							if (e.key === "ArrowDown") { e.preventDefault(); setI((x) => Math.min(items.length - 1, x + 1)); }
							else if (e.key === "ArrowUp") { e.preventDefault(); setI((x) => Math.max(0, x - 1)); }
							else if (e.key === "Enter" && items[i]) { e.preventDefault(); items[i].ir(); onCerrar(); }
							else if (e.key === "Escape") onCerrar();
						}} />
					<kbd>Esc</kbd>
				</div>
				<div className="ex-salto-lista" role="listbox">
					{items.map((x, k) => (
						<button type="button" key={x.k} role="option" aria-selected={k === i} className={k === i ? "on" : ""} onMouseEnter={() => setI(k)} onClick={() => { x.ir(); onCerrar(); }}>
							<b>{x.t}</b><small>{x.sub}</small>{x.pendientes ? <span className="badge-n">{x.pendientes}</span> : null}
						</button>
					))}
					{!items.length ? <div className="hp-vacio">Nada se llama «{q}».</div> : null}
				</div>
				<div className="ex-salto-pie"><span><kbd>↑</kbd><kbd>↓</kbd> moverte</span><span><kbd>Enter</kbd> abrir</span></div>
			</div>
		</div>
	);
};

/** Línea de "Fecha larga" para los programados. */
export const fechaProgramada = (iso: string) => fechaLarga(iso);
